import { Input, Select, Modal, Table, Space, Tag, App } from 'antd';
import { SearchOutlined, UserOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import execute from 'hooks/execute';
import { protectedResources } from 'config/msalConfig';
const UserSearchBox = () => {
    const { message } = App.useApp();
    const navigate = useNavigate();
    const [searchType, setSearchType] = useState('email');
    const [searchResults, setSearchResults] = useState([]);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const toBoolean = (value) => value === true || value === 1 || value === '1';
    const handleSearch = async (value) => {
        if (!value || value.trim() === '') {
            message.warning('Please enter a search term');
            return;
        }
        setLoading(true);
        try {
            const response = await execute(
                'GET',
                `${protectedResources.api.baseEndPoint}/customerUsers/searchAcrossOrganizations?searchTerm=${encodeURIComponent(value)}&searchType=${searchType}`
            );
            if (response.success) {
                setSearchResults(response.data || []);
                setIsModalOpen(true);
                if (response.data.length === 0) {
                    message.info('No users found matching your search criteria');
                }
            } else {
                message.error(response.message || 'Search failed');
            }
        } catch (error) {
            console.error('Search failed:', error);
            message.error('Failed to search users: ' + error.message);
        } finally {
            setLoading(false);
        }
    };
    const handleRowClick = (record) => {
        if (!record.Org_ID) {
            message.warning('Organization information not available for this user');
            return;
        }
        setIsModalOpen(false);
        // Check if this is an Org User
        if (record.IsOrgUser || !record.Customer_ID) {
            // Navigate to organization management page with highlight state
            navigate('/organization-management', {
                state: {
                    highlightOrgId: record.Org_ID
                }
            });
        } else {
            // Navigate to customer users page for regular customer users
            navigate(`/org-customers/${record.Org_ID}`, {
                state: {
                    expandCustomerId: record.Customer_ID,
                    highlightUserId: record.UserID
                }
            });
        }
    };
    // Get dynamic modal title based on search type
    const getSearchTitle = () => {
        const titles = {
            email: 'Email Search Results',
            name: 'Customer Name Search Results',
            organization: 'Organization Search Results'
        };
        return titles[searchType] || 'User Search Results';
    };
    const columns = [
        {
            title: 'Name',
            dataIndex: 'FirstName',
            key: 'name',
            render: (text, record) => `${record.FirstName || ''} ${record.LastName || ''}`.trim() || 'N/A',
            sorter: (a, b) => {
                const nameA = `${a.FirstName || ''} ${a.LastName || ''}`.trim().toLowerCase();
                const nameB = `${b.FirstName || ''} ${b.LastName || ''}`.trim().toLowerCase();
                return nameA.localeCompare(nameB);
            }
        },
        {
            title: 'Email',
            dataIndex: 'Email',
            key: 'email',
            sorter: (a, b) => (a.Email || '').localeCompare(b.Email || '')
        },
        {
            title: 'Organization',
            dataIndex: 'Org_Name',
            key: 'org',
            sorter: (a, b) => (a.Org_Name || '').localeCompare(b.Org_Name || '')
        },
        {
            title: 'Status',
            dataIndex: 'IsActive',
            key: 'status',
            render: (_, record) => {
                const isActive = toBoolean(record.IsActive);
                const isOnboarded = toBoolean(record.IsB2COnboarded);
                const statusText = !isActive ? 'Inactive' : (!isOnboarded ? 'Pending Onboard' : 'Active');
                const color = !isActive ? 'gray' : (!isOnboarded ? 'orange' : 'green');
                return <Tag color={color}>{statusText}</Tag>;
            },
            sorter: (a, b) => {
                const rank = (record) => {
                    const isActive = toBoolean(record.IsActive);
                    const isOnboarded = toBoolean(record.IsB2COnboarded);
                    if (!isActive) return 0;
                    if (!isOnboarded) return 1;
                    return 2;
                };
                return rank(a) - rank(b);
            }
        },
        {
            title: 'Admin',
            dataIndex: 'IsAdmin',
            key: 'admin',
            render: (isAdmin) => isAdmin ? 'Yes' : 'No',
            sorter: (a, b) => a.IsAdmin - b.IsAdmin
        },
        {
            title: 'Modules',
            dataIndex: 'Modules',
            key: 'modules',
            render: (modules) => modules || 'None'
        }
    ];
    return (
        <>
            <SearchContainer>
                <Select
                    value={searchType}
                    onChange={setSearchType}
                    style={{ width: 150, marginRight: 8 }}
                    options={[
                        { label: 'Email', value: 'email' },
                        { label: 'Customer Name', value: 'name' },
                        { label: 'Organization', value: 'organization' }
                    ]}
                />
                <Input.Search
                    placeholder="Search users across organizations..."
                    allowClear
                    enterButton={<SearchOutlined />}
                    size="large"
                    onSearch={handleSearch}
                    loading={loading}
                    style={{ width: 400 }}
                />
            </SearchContainer>
            <Modal
                title={
                    <Space>
                        <UserOutlined />
                        <span>{getSearchTitle()} ({searchResults.length})</span>
                    </Space>
                }
                open={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                footer={null}
                width={1000}
                destroyOnClose
            >
                <Table
                    columns={columns}
                    dataSource={searchResults}
                    rowKey="UserID"
                    pagination={{
                        pageSize: 10,
                        showTotal: (total) => `Total ${total} users`
                    }}
                    size="small"
                    onRow={(record) => ({
                        onClick: () => handleRowClick(record),
                        style: { cursor: 'pointer' }
                    })}
                />
            </Modal>
        </>
    );
};
const SearchContainer = styled.div`
    display: flex;
    align-items: center;
    margin-left: auto;
`;
export default UserSearchBox;
